import {
  collectAvailability,
  findPickPlayer,
  getPickTotalPoints,
  isPickSimulated
} from './aggregation';
import { applySubstitutions, resolveEffectiveMultipliers } from './auto-subs';
import type { LiveTeamPlayer } from './types';
import type { SubscribedMatchState } from './match-subscriber';
import type { LineupPlayer } from '@components/team-lineup';
import type { LivePlayer } from '@lib/live/match/types';

const toLiveEvents = (player: LivePlayer | null) => {
  if (!player) {
    return {
      goals: 0,
      assists: 0,
      yellowCards: 0,
      redCards: 0,
      missedPenalties: 0,
      bonusPoints: 0
    };
  }
  return {
    goals: player.scoring.goals?.count ?? 0,
    assists: player.scoring.assists?.count ?? 0,
    yellowCards: player.scoring.yellowCards?.count ?? 0,
    redCards: player.scoring.redCards?.count ?? 0,
    missedPenalties: player.scoring.penaltiesMissed?.count ?? 0,
    // bonusPoints.points carries the awarded bonus (0-3);
    // bonusPoints.count carries the BPS raw score, not the bonus.
    bonusPoints: player.scoring.bonusPoints?.points ?? 0
  };
};

type PlacedPick = {
  pick: LiveTeamPlayer;
  /** On the pitch rather than the bench, once substitutions are applied. */
  isInXi: boolean;
  multiplier: number;
  isCaptain: boolean;
};

const toLineupPlayer = (
  { pick, isInXi, multiplier, isCaptain }: PlacedPick,
  matchStates: Record<string, SubscribedMatchState>
): LineupPlayer => {
  const points = getPickTotalPoints(pick, matchStates);
  return {
    id: String(pick.id),
    displayName: pick.displayName,
    position: pick.position,
    team: { shortName: pick.teamShortName },
    kitImageUrl: pick.kitImageUrl,
    price: 0,
    fixtures: [],
    isCaptain,
    isViceCaptain: pick.isViceCaptain && !isCaptain,
    // Only the XI is multiplied. A bench player shows what they actually
    // scored, which is the point of looking at the bench.
    livePoints: isInXi ? points * multiplier : points,
    liveEvents: toLiveEvents(findPickPlayer(pick, matchStates)),
    isFaded: pick.optaMatchIds.length === 0,
    isSimulated: isPickSimulated(pick, matchStates)
  };
};

const bySlot = (a: LiveTeamPlayer, b: LiveTeamPlayer) => a.slot - b.slot;

/**
 * Builds the pitch from the team as it is actually scoring.
 *
 * A starter who blanked is shown on the bench and the substitute who replaced
 * them takes their place in the XI, so the pitch matches the running total
 * rather than the team as it was set up. The bench keeps its own order, with
 * each substitute's slot showing whoever it swapped with.
 */
export const buildLineups = (
  starting: LiveTeamPlayer[],
  bench: LiveTeamPlayer[],
  matchStates: Record<string, SubscribedMatchState>,
  activeChip: string | null
): { starting: LineupPlayer[]; bench: LineupPlayer[] } => {
  const picks = [...starting, ...bench];
  const { multipliers, substitutions, captainSlot } =
    resolveEffectiveMultipliers(
      picks,
      collectAvailability(picks, matchStates),
      activeChip
    );

  const place = (pick: LiveTeamPlayer, isInXi: boolean): PlacedPick => ({
    pick,
    isInXi,
    multiplier: multipliers.get(pick.slot) ?? 0,
    isCaptain: captainSlot === pick.slot
  });

  const placed = applySubstitutions(
    [...starting].sort(bySlot),
    [...bench].sort(bySlot),
    substitutions
  );

  // A substitute takes their own position's row, so the formation on screen
  // follows the swap rather than the team sheet.
  const xi = placed.starting.map((onPitch) =>
    toLineupPlayer(place(onPitch, true), matchStates)
  );
  const substitutes = placed.bench.map((benched) =>
    toLineupPlayer(place(benched, false), matchStates)
  );

  return { starting: xi, bench: substitutes };
};
